import { CountryCode, CountryCodeList } from 'types/countries';
import { CurrencyCode } from 'types/currencies';

/**
 * Default currency mapping for supported countries.
 * 
 * This is the source of truth for which currency each country uses.
 * Tenants automatically generate their currency map from their supported countries.
 */
const DEFAULT_COUNTRY_CURRENCY_MAP: Record<CountryCode, CurrencyCode> = {
  [CountryCodeList.CANADA]: CurrencyCode.CAD,
  [CountryCodeList.UNITED_STATES]: CurrencyCode.USD,
};

/**
 * Generates a currency map for a tenant based on its supported countries.
 * 
 * @param countries - Array of country codes supported by the tenant
 * @returns Currency map for the tenant
 * 
 * @example
 * ```ts
 * // US-only tenant
 * const currencyMap = createCurrencyMap([CountryCodeList.UNITED_STATES]);
 * // { US: 'USD' }
 * 
 * // Multi-country tenant
 * const currencyMap = createCurrencyMap([CountryCodeList.CANADA, CountryCodeList.UNITED_STATES]);
 * // { CA: 'CAD', US: 'USD' }
 * ```
 */
export function createCurrencyMap(
  countries: (string | CountryCode)[]
): Record<CountryCode, CurrencyCode> {
  const currencyMap: Record<string, CurrencyCode> = {};
  
  for (const country of countries) {
    const countryCode = country.toUpperCase() as CountryCode;
    const currency = DEFAULT_COUNTRY_CURRENCY_MAP[countryCode];
    
    if (!currency) {
      throw new Error(`No currency mapping found for country: ${countryCode}`);
    }
    
    currencyMap[countryCode] = currency;
  }
  
  return currencyMap as Record<CountryCode, CurrencyCode>;
}

/**
 * Resolves the currency for a given country code using the tenant's currency map.
 * Falls back to the tenant's default country currency if the country code is not found.
 * 
 * @param countryCode - The country code to resolve currency for (can be null/undefined)
 * @param currencyMap - The tenant's currency map
 * @param defaultCountry - The tenant's default country code
 * @returns The resolved currency code
 * 
 * @example
 * ```ts
 * const currency = getCurrencyForCountry('CA', tenant.localization.currencyMap, tenant.localization.defaultCountry);
 * // 'CAD'
 * ```
 */
export function getCurrencyForCountry(
  countryCode: string | null | undefined,
  currencyMap: Record<CountryCode, CurrencyCode>,
  defaultCountry: CountryCode
): CurrencyCode {
  if (!countryCode) {
    return currencyMap[defaultCountry];
  }
  
  const upperCode = countryCode.toUpperCase() as CountryCode;
  const currency = currencyMap[upperCode];
  
  if (!currency) {
    // Log warning when falling back - helps identify data issues
    console.warn('[CurrencyMap] Unknown country code, falling back to default', {
      requestedCountry: countryCode,
      defaultCountry,
      availableCountries: Object.keys(currencyMap),
    });
    return currencyMap[defaultCountry];
  }
  
  return currency;
}
